import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { brandUniverseGraphKey as graphKey } from "./universeCache";

/**
 * Named-rival mutations. Kept behind one small adapter so the presentation
 * never knows endpoint details and every successful write refreshes the graph.
 */
export function useUniverseCompetitors(brandId: string) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: graphKey(brandId) });

  const create = useMutation({
    mutationFn: async (name: string) => {
      await axiosInstance.post(`/brand/${brandId}/universe/entities`, {
        kind: "competitor",
        name,
      });
    },
    onSuccess: refresh,
  });

  const rename = useMutation({
    mutationFn: async (vars: { entityId: string; name: string }) => {
      await axiosInstance.patch(
        `/brand/${brandId}/universe/entities/${vars.entityId}`,
        { name: vars.name },
      );
    },
    onSuccess: refresh,
  });

  const merge = useMutation({
    mutationFn: async (vars: { sourceEntityId: string; targetEntityId: string }) => {
      await axiosInstance.post(
        `/brand/${brandId}/universe/entities/${vars.sourceEntityId}/merge`,
        { targetEntityId: vars.targetEntityId },
      );
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (entityId: string) => {
      await axiosInstance.delete(`/brand/${brandId}/universe/entities/${entityId}`);
    },
    onSuccess: refresh,
  });

  const fileFact = useMutation({
    mutationFn: async (vars: { factId: string; entityId: string }) => {
      await axiosInstance.patch(
        `/brand/${brandId}/universe/facts/${vars.factId}/entity`,
        { entityId: vars.entityId },
      );
    },
    onSuccess: refresh,
  });

  return { create, rename, merge, remove, fileFact };
}
